/**
 * Sender IDs CRUD via /v1/senders. The list query is the only thing the
 * page needs to subscribe to; mutations invalidate it on success so the
 * table refreshes automatically without a manual refetch.
 *
 * Polling is disabled (staleTime ~30s) because the catalog changes only
 * via operator action, not background events — unlike Messages where a
 * DLR can flip status at any moment.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type SenderType = 'long_code' | 'toll_free' | 'short_code' | 'alphanumeric';

export type RegistrationStatus =
  | 'not_required'
  | 'pending'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'suspended';

export interface SenderItem {
  id: number;
  type: SenderType;
  value: string;
  country_code: string;
  registration_status: RegistrationStatus;
  use_case?: string;
  monthly_fee: number;
  created_at: string;
}

export interface SenderListResponse {
  items: SenderItem[];
  total: number;
}

export interface CreateSenderInput {
  type: SenderType;
  value: string;
  country_code: string;
  use_case?: string;
  initial_status?: RegistrationStatus;
}

const SENDERS_KEY = ['senders', 'list'] as const;

export function useSendersList() {
  return useQuery({
    queryKey: SENDERS_KEY,
    queryFn: () => api.get<SenderListResponse>('/v1/senders'),
    staleTime: 30_000,
  });
}

export function useCreateSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSenderInput) => api.post<SenderItem>('/v1/senders', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SENDERS_KEY });
    },
  });
}

export function useUpdateSenderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, registration_status }: { id: number; registration_status: RegistrationStatus }) =>
      api.patch<SenderItem>(`/v1/senders/${id}/status`, { registration_status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SENDERS_KEY });
    },
  });
}

export function useDeleteSender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/senders/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SENDERS_KEY });
    },
  });
}
